import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../store/slices/authSlice';
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { BsShieldCheck } from 'react-icons/bs';
import { SeoHead } from '../utils/seo';
import { SupportButton } from '../components/ui/SupportButton';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

export const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.auth);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    // Clear error when component unmounts
    return () => {
      dispatch(clearError());
      setLoginError(null);
    };
  }, [dispatch]);

  // Update local error state when Redux error changes
  useEffect(() => {
    if (error) {
      setLoginError(error.message || 'Invalid email or password');
    }
  }, [error]);

  const onSubmit = async (data) => {
    try {
      setLoginError(null); // Clear any previous errors
      const result = await dispatch(login(data));
      
      if (login.fulfilled.match(result)) {
        // Redirect to admin dashboard if user is admin, otherwise to user dashboard
        const redirectPath = result.payload.user.role === 'admin' ? '/admin' : '/dashboard';
        navigate(redirectPath);
      }
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const pageTitle = 'Login | Nitro Logs - Premium Social Media Accounts';
  const pageDescription = 'Log in to Nitro Logs to manage your verified social media accounts, boosting services, and virtual number purchases with secure payments.';

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords="login, Nitro Logs login, verified social media accounts login, secure account access"
      />

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 luxury-dot-pattern opacity-30" />
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-primary-50 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-96 bg-gradient-to-t from-secondary-50 to-transparent" />
        
        {/* Floating Shapes */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-20 w-64 h-64 bg-primary-200 rounded-full blur-3xl opacity-20"
        />
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-secondary-200 rounded-full blur-3xl opacity-20"
        />

        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="max-w-md w-full relative z-10"
        >
          {/* Logo */}
          <motion.div variants={fadeInUp} className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 group">
              <motion.div
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
                className="w-14 h-14 rounded-xl flex items-center justify-center"
              >
                <img src="/nitropng.png" alt="Logo" className="w-full h-full text-white" />
              </motion.div>
              <span className="text-3xl font-display font-bold">
                <span className="gradient-text">Nitro</span>
                <span className="text-luxury-900">Logs</span>
              </span>
            </Link>
          </motion.div>

          {/* Welcome Text */}
          <motion.div variants={fadeInUp} className="text-center mb-8">
            <h2 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-luxury-600">
              Sign in to access your account and continue your journey
            </p>
          </motion.div>

          {/* Main Card */}
          <motion.div
            variants={fadeInUp}
            className="glass border border-luxury-200 rounded-2xl p-8 shadow-luxury-xl"
          >
            {/* Error Message */}
            {loginError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
              >
                {loginError}
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-luxury-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                  <input
                    type="email"
                    {...register('email')}
                    className={`input-luxury pl-12 ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-luxury-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    className={`input-luxury pl-12 pr-12 ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-luxury-400 hover:text-luxury-600 transition-colors"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="w-4 h-4 text-primary-500 rounded border-luxury-300 focus:ring-primary-500" />
                  <span className="text-sm text-luxury-600">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full luxury-button disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Signing in...
                  </div>
                ) : (
                  <>
                    Sign In
                    <FiArrowRight className="inline ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>

          {/* Sign Up Link */}
          <motion.p
            variants={fadeInUp}
            className="text-center mt-8 text-luxury-600"
          >
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              Sign up now
            </Link>
          </motion.p>
        </motion.div>
      </div>      <SupportButton />    </>
  );
};